// Sizing a table column to its content.
//
// DynamicTable lays out with table-layout:fixed, so a column never grows to fit
// its widest cell on its own — the width it is given is the width it gets, and
// anything longer is ellipsised. Columns that are specced as "as wide as the
// longest value on this page" therefore have to be measured up front.
//
// Measurement goes through a canvas 2D context rather than a hidden DOM node:
// measureText costs no layout, so this can run straight from render while the
// column list is being built.

// Cells are 12px/400 Montserrat, headers 12px/700 (see .entities-table th,td in
// scss/volt/pages/company.scss). Keep these in sync with that rule.
const CELL_FONT = '400 12px Montserrat, sans-serif';
const HEADER_FONT = '700 12px Montserrat, sans-serif';
// Chips (Type, Condition, Set Type …) render their label at 600.
export const CHIP_FONT = '600 12px Montserrat, sans-serif';
// A chip that isn't pinned to a mapped width sizes to fit-content, i.e. its own
// 10px horizontal padding around the label.
export const CHIP_TEXT_PADDING = 20;

// th/td padding is 10px each side.
const CELL_PADDING = 20;
// A sortable header renders label + 4px gap + a 12px sort arrow, and the span
// carries 8px of right padding to keep the arrow clear of the resize grip.
const SORT_ICON_SPACE = 4 + 12 + 8;

let ctx;
const getCtx = () => {
  if (ctx !== undefined) return ctx;
  ctx = typeof document !== "undefined"
    ? document.createElement("canvas").getContext("2d")
    : null;
  return ctx;
};

export const measureTextWidth = (text, font = CELL_FONT) => {
  const c = getCtx();
  if (!c || text === null || text === undefined || text === "") return 0;
  c.font = font;
  return c.measureText(String(text)).width;
};

/**
 * Room a header needs on its own: its label, the cell padding, and the sort
 * arrow. Useful as a column's `minWidth` — an auto-sized column can then always
 * be dragged back down to the point where its title still reads, but no further.
 */
export const getHeaderWidth = (label, { sortable = true } = {}) =>
  Math.ceil(
    measureTextWidth(label, HEADER_FONT) + CELL_PADDING + (sortable ? SORT_ICON_SPACE : 0)
  );

// Shared tail: turn the widest cell content box into a column width, never
// letting it fall below what the header itself needs.
//
// +1 covers sub-pixel rounding — measureText can land a hair under the width the
// browser actually paints, which shows up as a stray ellipsis.
const toColumnWidth = (contentBox, { label, sortable, min, max }) => {
  const contentWidth = contentBox > 0 ? contentBox + CELL_PADDING : 0;
  const needed = Math.ceil(Math.max(contentWidth, getHeaderWidth(label, { sortable }))) + 1;
  return `${Math.min(max, Math.max(min, needed))}px`;
};

/**
 * Width a text column needs so the longest value on the current page renders in
 * full.
 *
 * @param {Array}    rows      the page's rows
 * @param {Function} accessor  row -> the text rendered in the cell
 * @param {Object}   opts
 * @param {string}   opts.label     header text, so a short column still fits its own title
 * @param {number}   opts.extra     non-text chrome inside the cell (avatar + its margin)
 * @param {boolean}  opts.sortable  header reserves room for the sort arrow
 * @param {number}   opts.min       floor; pass 0 to let the header be the only lower bound
 * @param {number}   opts.max       safety cap — one pathological value shouldn't
 *                                  push a column past the viewport
 * @returns {string} a px width for col.width
 */
export const getContentColumnWidth = (
  rows,
  accessor,
  { label = "", extra = 0, sortable = true, min = 120, max = 480 } = {}
) => {
  let widest = 0;
  (rows || []).forEach((row) => {
    const w = measureTextWidth(accessor(row));
    if (w > widest) widest = w;
  });
  return toColumnWidth(widest > 0 ? widest + extra : 0, { label, sortable, min, max });
};

/**
 * Same, for a column whose cell is a chip. Chips are often pinned to a mapped
 * width rather than sized from their label, so the caller supplies the chip's
 * rendered width per row instead of the text.
 *
 * @param {Array}    rows          the page's rows
 * @param {Function} chipWidthFor  row -> the chip's rendered px width
 */
export const getChipColumnWidth = (
  rows,
  chipWidthFor,
  { label = "", sortable = true, min = 0, max = 480 } = {}
) => {
  let widest = 0;
  (rows || []).forEach((row) => {
    const w = chipWidthFor(row) || 0;
    if (w > widest) widest = w;
  });
  return toColumnWidth(widest, { label, sortable, min, max });
};
